import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import axios from "axios";
import { getFieldErrors } from "../helpers";
import { IUser, tryConfirmLogin } from "./authReducer";
import { IEnumCompany } from "./companiesReducer";

export interface IEnumCustomer {
    _id: string;
    userId: string;
    firstName: string;
    lastName: string;
    email: string;
    phoneNumber: string;
    imgSrc: string;
    emailVerified: boolean;
    status: string;
    has2FactorAuth: boolean;
}

export interface ICustomer extends IUser {
    userId: string;
    deliveryAddress: string;
    ordersCount: number;
    orderRequestsCount: number;
    creditBalance: number;
    company?: IEnumCompany;
}


export interface ICustomersState {
    fetchingMainCustomer: boolean;
    fetchMainCustomerErr: string | null;
    mainCustomerId: string | null;
    mainCustomer: ICustomer | null;
}

const initialState = {
    fetchingMainCustomer: false,
    fetchMainCustomerErr: null,
    mainCustomerId: null,
    mainCustomer: null,
} as ICustomersState;

const customersSlice = createSlice({
    name: 'customers',
    initialState,
    reducers: {
        clearFetchMainCustomerErr: (state: ICustomersState) => {
            state.fetchMainCustomerErr = null;
        },
        fetchMainCustomerStart: (state: ICustomersState) => {
            state.fetchingMainCustomer = true;
            state.fetchMainCustomerErr = null;
        },
        fetchMainCustomerPass: (state: ICustomersState, action: PayloadAction<ICustomer>) => {
            state.fetchingMainCustomer = false;
            state.mainCustomer = action.payload;
        },
        fetchMainCustomerFail: (state: ICustomersState, action: PayloadAction<string>) => {
            state.fetchingMainCustomer = false;
            state.fetchMainCustomerErr = action.payload;
        },
        setMainCustomerId: (state: ICustomersState, action: PayloadAction<string | null>) => {
            state.mainCustomerId = action.payload;
        },
        setMainCustomer: (state: ICustomersState, action: PayloadAction<ICustomer | null>) => {
            state.mainCustomer = action.payload;
        },
    }
});

export const  {
    clearFetchMainCustomerErr,
    fetchMainCustomerStart,
    fetchMainCustomerPass,
    fetchMainCustomerFail,
    setMainCustomer,
    setMainCustomerId,
} = customersSlice.actions;

interface ITryFetchMainCustomerArg {
    authToken: string;
    customerId: string;
}

export const tryFetchMainCustomer = createAsyncThunk('customers/tryFetchMainCustomer', (arg: ITryFetchMainCustomerArg, { dispatch }) => {
    const { authToken, customerId } = arg;

    const endpoint = `${process.env.REACT_APP_SERVER_ENDPOINT}/api/customers/${customerId}`;

    dispatch(fetchMainCustomerStart());
    axios.get(endpoint, {
        headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${authToken}`
        }
    }).then(res => {
        const data = res.data;

        dispatch(fetchMainCustomerPass(res.data));
    }).catch(err => {
        const data = err?.response?.data;

        if (data?.err && (data.err === "Session expired")) {
            dispatch(tryConfirmLogin({ authToken }));
            return;
        }

        if (data?.errors) {
            dispatch(fetchMainCustomerFail(getFieldErrors(data.errors)[0].msg));
        } else if (data?.err) {
            dispatch(fetchMainCustomerFail(data.err));
        } else {
            dispatch(fetchMainCustomerFail('Check your network connection.'));
        }
    });
});

export default customersSlice.reducer;